import TrustedShopsLogo from "../../icons/TrustedShops.svg";
import TrustedPilotLogo from "../../icons/TrustedPilot.svg";
import Stars from "../../images/trustpilotStars.svg";
import RefreshIcon from "../../icons/Refresh.svg";
import LockIcon from "../../icons/Lock-Darkgray.svg";
import BagIcon from "../../icons/Shopping-Bag.svg";
import { translate } from "../../utils/translator";
import TrustBadgeV3 from "./TrustBadgeV3";
import { TrustProvider } from "./trustBadgeLocales";


const DISALLOWED_TRUSTEDSHOP_COUNTRIES = ['CH'];

// variant="v3" is the Care+ payment badge, one design for every country (see trustBadgeLocales.tsx).
// Without it this renders the classic order-sidebar badge, which is not locale-driven.
export default function TrustedShopsBadge({ className, country, language, variant, provider }: { className: string, country: string, language: string, variant?: "v3", provider?: TrustProvider }) {

  if (variant === "v3") {
    return <TrustBadgeV3 className={className} country={country} language={language} provider={provider} />;
  }

  const isUk = country?.toUpperCase() === 'GB';
  const isTrustpilotLogo = isUk || DISALLOWED_TRUSTEDSHOP_COUNTRIES.includes(country?.toUpperCase());
  // UK shows a rating ("Excellent") with the Trustpilot logo instead of the Trusted Shops name.
  const serviceTitle = isUk
    ? 'Excellent'
    : DISALLOWED_TRUSTEDSHOP_COUNTRIES.includes(country?.toUpperCase()) ? 'Trustedpilot' : 'Trusted Shops';

  const featureIconClass = "w-[20px] h-[20px] shrink-0 rounded-full flex items-center justify-center";
  const featureIconStyle = { background: "linear-gradient(113.46deg, #F7FAEF 5.59%, #c9e4b3 105.24%)" };

  return (
    <div id="trusted-shops-badge" className={`p-1 bg-white border border-gray-200 rounded-md max-w-[500px] shadow-sm flex flex-col relative ${className}`} style={{ borderLeft: '5px solid var(--border-focus)' }}>

      {/* Header Section */}
      <div id="trusted-shops-header" className="flex items-center justify-between mb-1 max-[370px]:flex-col">

        <div id="trusted-shops-info" className="flex items-center gap-3">
          <div id="trusted-shops-details">

            <div className="flex flex-row items-center gap-1 min-[370px]:ml-2">
              <img id="trusted-shops-logo" src={isTrustpilotLogo ? TrustedPilotLogo : TrustedShopsLogo} alt="title" className="w-5 h-5" />
              <h3 id="trusted-shops-title" className="font-medium text-black text-lg leading-tight break-normal hyphens-none">{serviceTitle}</h3>
            </div>

            <div id="trusted-shops-rating" className="flex items-center gap-1 ml-8">
              <img id="trusted-shops-stars" src={Stars} alt="stars" className="w-[64px] h-[12px]" />
              <span id="trusted-shops-score" className="text-xs font-medium text-[#5F5F5F] ml-1">4.8/5</span>
            </div>
          </div>
        </div>

        <div id="trusted-shops-stats">
          <div className="flex items-center">
            <div id="treatments-counter" className="px-2 py-1 rounded-full flex flex-col items-center mt-1 min-[370px]:mr-1" style={{
              width: '165px',
              height: '45px',
              background: 'linear-gradient(113.46deg,#c9e4b3 5.59%, #F7FAEF 105.24%)',
              border: '0.851485px solid #FFFFFF',
              boxShadow: '0px 3.40594px 5.19406px rgba(0, 0, 0, 0.08)',
              borderRadius: '12.7723px',
            }}>
              <span id="treatments-number" className="text-lg font-medium font-prompt text-black leading-[1.2]">1.5M+ <span className="font-semibold font-assistant text-[11px]">{translate(language, "payment", "trustedShopsPilot/label_1")}</span></span>
              <span id="treatments-location" className="text-[10px] text-[#505050] font-light">{translate(language, "payment", "trustedShopsPilot/label_2")}</span>
            </div>
          </div>
        </div>

      </div>


      {/* Features Section */}
      <div id="trusted-shops-features" className="flex flex-row items-center justify-between max-[350px]:flex-col ml-2 mt-1 gap-2">

        <div id="money-back-feature" className="flex items-center gap-1">
          <div id="money-back-icon-container" className={featureIconClass} style={featureIconStyle}>
            <img id="money-back-icon" src={RefreshIcon} alt="Money-Back guarantee" className="w-[10px]" />
          </div>
          <span id="money-back-line" className="text-[11px] text-[#656D79] font-normal font-roboto min-[350px]:max-w-[70px]">{translate(language, "payment", "trustedShopsPilot/label_3")}</span>
        </div>

        <div id="secure-data-feature" className="flex items-center gap-1">
          <div id="secure-data-icon-container" className={featureIconClass} style={featureIconStyle}>
            <img id="secure-data-icon" src={LockIcon} alt="Secure & encrypted data" className="w-[10px]" />
          </div>
          <span id="secure-data-line" className="text-[11px] text-[#656D79] font-normal font-roboto min-[350px]:max-w-[110px]">{translate(language, "payment", "trustedShopsPilot/label_4")}</span>
        </div>

        <div id="discreet-packaging-feature" className="flex items-center gap-1">
          <div id="discreet-packaging-icon-container" className={featureIconClass} style={featureIconStyle}>
            <img id="discreet-packaging-icon" src={BagIcon} alt="Discreet packaging" className="w-[10px]" />
          </div>
          <span id="discreet-packaging-line" className="text-[11px] text-[#656D79] font-normal font-roboto min-[350px]:max-w-[70px]">{translate(language, "payment", "trustedShopsPilot/label_5")}</span>
        </div>

      </div>
    </div>
  );
}
